import { EndpointBuilder } from '@reduxjs/toolkit/dist/query/endpointDefinitions.d';
import { createApi } from '@reduxjs/toolkit/dist/query/react';

import { baseQueryWithIntercept } from '@apis/fetchBaseQuery';
import {
    REQUESTS_PATH,
    REQUESTS_PATH_DEFECTIVE
} from '@apis/outboundOrderByWarehouse/OutboundOrderByWarehouse.types';
import { QueryWithIntercept } from '@apis/queryWithIntercept';
import {
    GetWarehouseResponse,
    GetAccountWarehouseDTO,
    GetWarehouseTransformedResponse
} from '@apis/warehouse/WarehouseApi.types';
import {
    GetBasicDtoPagination,
    OrderReferenceLabelsResponse,
    PreCheckDTO,
    PreCheckResponse,
    SubmitFullOrderResponse,
    SubmitOrderDTO,
    SubmitReducedOrderResponse
} from '@customTypes/api/api.types';
import {
    AvailableServicesDTO,
    AvailableServicesResponse
} from '@customTypes/deliveryServices/deliveryServices.types';
import {
    GetProductTransformedResponse,
    GetSerialsTransformedResponse
} from '@customTypes/general/product.types';
import { OrderReferenceLabelsDTO } from '@customTypes/order/order.types';
import { getServicesWithIds } from '@helpers/activeOrder';
import {
    getSerialsQuery,
    getTransformedDataWithHazard,
    getTransformedSerialNumbersData
} from '@helpers/apiFormatter';
import { getBasePaginationBody } from '@helpers/getBasePaginationBody';

const getCommonEndpoints = (
    builder: EndpointBuilder<QueryWithIntercept, never, string>,
    requestsPath: typeof REQUESTS_PATH | typeof REQUESTS_PATH_DEFECTIVE
) => ({
    getServices: builder.mutation<
        AvailableServicesResponse,
        AvailableServicesDTO
    >({
        query: (dto) => ({
            url: requestsPath.GET_SERVICES,
            method: 'POST',
            body: dto
        }),
        transformResponse(response: AvailableServicesResponse) {
            return getServicesWithIds(response) as AvailableServicesResponse;
        }
    }),
    getOrderReferenceNumberLabels: builder.mutation<
        OrderReferenceLabelsResponse,
        OrderReferenceLabelsDTO
    >({
        query: (dto) => ({
            url: requestsPath.ORDER_REFERENCE_LABELS,
            method: 'POST',
            body: dto
        })
    }),
    getSerialNumbers: builder.mutation<
        GetSerialsTransformedResponse,
        GetBasicDtoPagination
    >({
        query: (dto) => getSerialsQuery(dto, requestsPath.SERIAL_NUMBERS),
        transformResponse: getTransformedSerialNumbersData
    }),
    preCheckByWarehouse: builder.mutation<PreCheckResponse, PreCheckDTO>({
        query: (dto) => {
            return {
                url: requestsPath.PRE_CHECK,
                method: 'POST',
                body: dto
            };
        }
    }),
    submitFullOrderByWarehouse: builder.mutation<
        SubmitFullOrderResponse,
        SubmitOrderDTO
    >({
        query: (dto) => ({
            url: requestsPath.SUBMIT_FULL_ORDER,
            method: 'POST',
            body: dto
        }),
        extraOptions: {
            flowEnd: true
        }
    }),
    submitReducedOrder: builder.mutation<
        SubmitReducedOrderResponse,
        SubmitOrderDTO
    >({
        query: (dto) => ({
            url: requestsPath.SUBMIT_REDUCED_ORDER,
            method: 'POST',
            body: dto
        }),
        extraOptions: {
            flowEnd: true
        }
    }),
    getAccountWarehouse: builder.mutation<
        GetWarehouseTransformedResponse,
        GetAccountWarehouseDTO
    >({
        query(dto) {
            return {
                url: requestsPath.ACCOUNT_WAREHOUSE,
                method: 'POST',
                body: dto
            };
        },
        extraOptions: {
            flowStart: true
        },
        transformResponse: (
            response: GetWarehouseResponse
        ): GetWarehouseTransformedResponse => {
            return {
                ...response,
                data: {
                    available: response.data?.available?.map((item) => ({
                        ...item,
                        id: item.sslId.toString()
                    })),
                    unavailable: response.data?.unavailable?.map((item) => ({
                        ...item,
                        id: item.sslId.toString()
                    }))
                }
            };
        }
    })
});

const getCustomDefectiveEndpoints = (
    builder: EndpointBuilder<QueryWithIntercept, string, string>
) => ({
    getWarehouseProducts: builder.mutation<
        GetProductTransformedResponse,
        GetBasicDtoPagination
    >({
        query: (dto) => {
            return {
                url: REQUESTS_PATH_DEFECTIVE.INVENTORY,
                method: 'POST',
                body: getBasePaginationBody({
                    ...dto,
                    filterFields: 'CustomDescription',
                    fixedSorter: 'partNumber'
                })
            };
        },
        transformResponse: getTransformedDataWithHazard
    })
});

export const outboundOrderByWarehouseApi = createApi({
    reducerPath: 'outboundOrderByWarehouseApi',
    baseQuery: baseQueryWithIntercept,
    endpoints: (builder) => getCommonEndpoints(builder, REQUESTS_PATH)
});

export const outboundOrderByWarehouseDefectiveApi = createApi({
    reducerPath: 'outboundOrderByWarehouseDefectiveApi',
    baseQuery: baseQueryWithIntercept,
    endpoints: (builder) => ({
        ...getCommonEndpoints(builder, REQUESTS_PATH_DEFECTIVE),
        ...getCustomDefectiveEndpoints(builder)
    })
});

export const {
    usePreCheckByWarehouseMutation,
    useGetServicesMutation,
    useGetOrderReferenceNumberLabelsMutation,
    useGetSerialNumbersMutation,
    useSubmitFullOrderByWarehouseMutation,
    useSubmitReducedOrderMutation,
    useGetAccountWarehouseMutation
} = outboundOrderByWarehouseApi;

export const {
    usePreCheckByWarehouseMutation: useDefectivePreCheckByWarehouseMutation,
    useGetServicesMutation: useDefectiveGetServicesMutation,
    useGetOrderReferenceNumberLabelsMutation:
        useDefectiveGetOrderReferenceNumberLabelsMutation,
    useGetSerialNumbersMutation: useDefectiveGetSerialNumbersMutation,
    useSubmitFullOrderByWarehouseMutation:
        useDefectiveSubmitFullOrderByWarehouseMutation,
    useSubmitReducedOrderMutation: useDefectiveSubmitReducedOrderMutation,
    useGetAccountWarehouseMutation: useDefectiveGetAccountWarehouseMutation,
    useGetWarehouseProductsMutation: useDefectiveGetWarehouseProductsMutation
} = outboundOrderByWarehouseDefectiveApi;
